// POST /invites · GET /invites/:token · POST /invites/:token/revoke ·
// POST /invites/:token/accept · GET /invites · GET /invites/pending
import { z } from 'zod'
import { genderSchema, linkTypeSchema } from '../enums.js'
import { isoDateTimeSchema, okResponseSchema } from './common.js'
import { linkDtoSchema } from './link.js'

export const inviteDtoSchema = z.object({
  token: z.string(),
  type: linkTypeSchema,
  created_at: isoDateTimeSchema,
  expires_at: isoDateTimeSchema, // created_at + INVITE_TTL_DAYS
  url: z.string(), // t.me/<bot>?startapp=inv_<token>
})
export type InviteDto = z.infer<typeof inviteDtoSchema>

// POST /invites — «Кто это для тебя?», создаёт одноразовую ссылку. Тратит слот лимита.
export const createInviteRequestSchema = z.object({
  type: linkTypeSchema,
})
export type CreateInviteRequest = z.infer<typeof createInviteRequestSchema>
export const createInviteResponseSchema = z.object({
  token: z.string(),
  url: z.string(),
})
export type CreateInviteResponse = z.infer<typeof createInviteResponseSchema>

/**
 * GET /invites/:token — превью приглашения для экрана B0: кто зовёт, ДО принятия.
 *
 * Отдаёт ровно два поля о пригласившем — имя и пол, — и ничего больше: токен приходит по ссылке
 * из мессенджера, то есть на эту ручку может постучаться кто угодно. Пол нужен не для украшения,
 * а чтобы Луня не обращалась к позвавшему в женском роде («Дима утащила тебя к себе на небо»);
 * `null` — пол не указан, тогда берётся формулировка без рода.
 */
export const invitePreviewResponseSchema = z.object({
  token: z.string(),
  inviter_first_name: z.string(),
  inviter_gender: genderSchema.nullable(),
})
export type InvitePreviewResponse = z.infer<typeof invitePreviewResponseSchema>

// POST /invites/:token/revoke — token в пути, тела нет.
export const revokeInviteResponseSchema = okResponseSchema
export type RevokeInviteResponse = z.infer<typeof revokeInviteResponseSchema>

/**
 * Чем закончился переход по ссылке-приглашению.
 *
 * Это **не** коды ошибок. Приглашение — вирусная петля продукта, и «ничего не произошло» —
 * худший из возможных исходов (правка основателя). Поэтому `POST /invites/:token/accept`
 * отвечает 200 на все предсказуемые случаи и называет исход полем, а не бросает `AppError`:
 * ошибка транспорта и «ссылка уже погасла» — разные вещи, и путать их клиенту нельзя.
 * `AppError` остаётся только за настоящими сбоями (авторизация, 5xx).
 *
 * | Исход | Когда |
 * | --- | --- |
 * | `ACCEPTED` | связь создана прямо сейчас |
 * | `ALREADY_LINKED` | связь между этими двумя уже есть — в том числе повторный переход по своей же принятой ссылке |
 * | `SELF` | человек открыл собственное приглашение |
 * | `TAKEN` | ссылку уже использовал кто-то другой (одноразовость) |
 * | `EXPIRED` | вышел срок (`INVITE_TTL_DAYS`) |
 * | `REVOKED` | пригласившая отозвала |
 * | `NOT_FOUND` | токена нет вовсе: обрезали ссылку, опечатались, аккаунт удалён |
 * | `LIMIT_INVITER` | у пригласившей все нити заняты (подписка кончилась после отправки) |
 * | `LIMIT_INVITEE` | у пришедшего все нити заняты — только при `GUEST_LINK_COUNTS_AGAINST_LIMIT` |
 */
export const INVITE_OUTCOMES = [
  'ACCEPTED',
  'ALREADY_LINKED',
  'SELF',
  'TAKEN',
  'EXPIRED',
  'REVOKED',
  'NOT_FOUND',
  'LIMIT_INVITER',
  'LIMIT_INVITEE',
] as const
export const inviteOutcomeSchema = z.enum(INVITE_OUTCOMES)
export type InviteOutcome = z.infer<typeof inviteOutcomeSchema>

/**
 * POST /invites/:token/accept — принятие приглашения. Гостевой слот не тратит (04 §2.9).
 *
 * **Идемпотентна.** Повторный вызов с тем же токеном тем же человеком не создаёт вторую связь
 * и не ломает состояние: он возвращает `ALREADY_LINKED` и ту же самую связь. Это не роскошь —
 * `start_param` приезжает при каждом холодном старте мини-аппа, и человек, открывший ссылку
 * дважды, обязан получить один и тот же ответ.
 *
 * Имя и пол пригласившей приходят в ответе всегда, когда сервер их знает, — включая неудачные
 * исходы: «попроси у Иры новую ссылку» без имени не собрать.
 */
export const acceptInviteResponseSchema = z.object({
  outcome: inviteOutcomeSchema,
  /** Связь — при `ACCEPTED` и `ALREADY_LINKED`, иначе `null`. */
  link: linkDtoSchema.nullable(),
  inviter_first_name: z.string().nullable(),
  inviter_gender: genderSchema.nullable(),
  /** Лимит той стороны, у которой он упёрся. Есть только при `LIMIT_*`. */
  limit: z.number().int().nullable(),
})
export type AcceptInviteResponse = z.infer<typeof acceptInviteResponseSchema>

// GET /invites — мои ожидающие (неиспользованные, неотозванные, не просроченные) приглашения.
export const listInvitesResponseSchema = z.object({
  items: z.array(inviteDtoSchema),
})
export type ListInvitesResponse = z.infer<typeof listInvitesResponseSchema>

/**
 * GET /invites/pending — приглашение, которое этот человек уже открывал, но ещё не принял.
 *
 * Запасной путь на случай, когда `start_param` до мини-аппа не доехал: Telegram отдаёт его
 * только при запуске и только один раз, а бот, получивший `/start inv_<token>`, знает и токен,
 * и того, кто по нему пришёл. Серверная отметка об открытии переживает и потерянный параметр,
 * и переустановку клиента, и смену устройства — то есть ровно то, чего не переживает ссылка.
 */
export const pendingInviteResponseSchema = z.object({
  token: z.string().nullable(),
  inviter_first_name: z.string().nullable(),
  inviter_gender: genderSchema.nullable(),
})
export type PendingInviteResponse = z.infer<typeof pendingInviteResponseSchema>
